import { z } from 'zod';
import { AI_PROVIDER_TYPES_WRITABLE, NIM_DEPLOYMENTS } from '../constants/aiModels.js';
import { AI_PROFILE_TYPES, CLAUDE_CLI_PERMISSION_MODES } from '../constants/aiProfile.js';
import { chatModelIdSchema } from './aiChat.schema.js';
// --- AI Profile Schemas ---
//
// WIRED at the write boundary (#2581): `ai.controller.ts` createProfile/updateProfile/import and the
// `ai_profiles.create` / `.update` / `.import` tool handlers parse the caller's body with
// `createProfileSchema` / `updateProfileSchema` / `importProfileSchema` (gh#2654) BEFORE any side effect (api
// `modules/ai/aiProfileWriteBoundary.ts`). This is the PATCH-level door; aiProfile.service keeps
// its own gates on the MERGED object (stored row + patch), which a patch parse cannot see.
// Both arms are `.strict()`: a key in the wrong arm (e.g. `serviceAccountJson` under apiKey
// auth) is a 400, not a silent strip of a secret the controller then never sees.
// apiKey branch: Express-mode API key auth (default for all providers including Vertex Express)
const providerConfigApiKeySchema = z.object({
    authMode: z.literal('apiKey').optional().default('apiKey'),
    apiKey: z.string().max(500).optional(),
    baseUrl: z.string().url().max(500).optional(),
    sdkType: z.enum(['openai', 'anthropic', 'gemini']).optional(),
    // Vertex-specific optional fields (Express mode: can be omitted)
    projectId: z.string().max(100).optional(),
    region: z.string().max(50).optional(),
    // NIM-specific. Declared HERE (not only in nimProviderConfigSchema) so the arm accepts it;
    // off-nim it is refused by the superRefine below.
    deployment: z.enum(NIM_DEPLOYMENTS).optional(),
}).strict();
// serviceAccount branch: GCP Service Account JSON (Vertex AI SA auth)
const providerConfigServiceAccountSchema = z.object({
    authMode: z.literal('serviceAccount'),
    // Write-only secret: the HTTP controller extracts it and stores it encrypted OUTSIDE settings,
    // and the editor never echoes it back — so an update of an SA-configured profile carries
    // `authMode:'serviceAccount'` WITHOUT it (3 stored vertex rows have exactly that shape).
    // Validated whenever present; presence is not required. On create, absence means a typed
    // error at dial time (vertex.provider.ts) — the NIM-style key gate was deliberately not added.
    serviceAccountJson: z.string().min(50, 'SA JSON çok kısa').refine((s) => {
        try {
            const j = JSON.parse(s);
            return j.type === 'service_account' && !!j.private_key && !!j.client_email;
        }
        catch {
            return false;
        }
    }, 'SA JSON malformed or missing required fields (type, private_key, client_email)').optional(),
    projectId: z.string().min(1, 'projectId zorunlu').max(100),
    // No default: the service replaces `providerConfig` WHOLESALE on update, so a default here
    // would rewrite a stored `region:'global'` (3 rows) to `us-central1` from a region-less patch.
    // The reader owns the fallback (vertex.provider.ts: `config.region || 'us-central1'`).
    region: z.string().max(50).optional(),
    sdkType: z.literal('gemini'),
    baseUrl: z.string().url().max(500).optional(),
}).strict();
// Back-compat: inject authMode='apiKey' when field is missing (old profiles without authMode)
const providerConfigSchema = z.preprocess((val) => {
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        const obj = val;
        if (!obj.authMode)
            return { authMode: 'apiKey', ...obj };
    }
    return val;
}, z.discriminatedUnion('authMode', [
    providerConfigApiKeySchema,
    providerConfigServiceAccountSchema,
])).optional();
// ---------------------------------------------------------------------------
// NVIDIA NIM — provider-scoped rules (gh#2579)
//
// NIM has two deployments with opposite constraints, so the shape is a
// discriminated union rather than a bag of optionals:
//   hosted      — endpoint is FIXED (NIM_HOSTED_BASE_URL). A caller-supplied
//                 baseUrl here would redirect the profile's API key to an
//                 arbitrary host, so it is rejected rather than ignored.
//   self-hosted — baseUrl is REQUIRED (there is no default container address).
//
// A missing `deployment` matches neither arm and fails the discriminator —
// fail-closed by construction, mirroring createProvider's refusal to guess.
// ---------------------------------------------------------------------------
const NIM_SELF_HOSTED_URL_MESSAGE = 'Self-hosted NIM baseUrl http(s) olmalı ve userinfo (kullanıcı adı/şifre) içeremez';
/** http(s) only, and no `user:pass@` credentials smuggled into the host. */
function isPlainHttpUrl(value) {
    let parsed;
    try {
        parsed = new URL(value);
    }
    catch {
        return false;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
        return false;
    return parsed.username === '' && parsed.password === '';
}
const nimHostedConfigSchema = z.object({
    deployment: z.literal('hosted'),
    authMode: z.literal('apiKey').optional(),
    apiKey: z.string().max(500).optional(),
    // NIM hosted speaks the OpenAI wire format only.
    sdkType: z.literal('openai').optional(),
    baseUrl: z.undefined({
        invalid_type_error: 'NIM hosted deployment sabit endpoint kullanır; baseUrl gönderilemez',
    }),
});
const nimSelfHostedConfigSchema = z.object({
    deployment: z.literal('self-hosted'),
    authMode: z.literal('apiKey').optional(),
    apiKey: z.string().max(500).optional(),
    sdkType: z.literal('openai').optional(),
    baseUrl: z
        .string()
        .min(1, 'Self-hosted NIM için baseUrl zorunlu')
        .max(500)
        .refine(isPlainHttpUrl, NIM_SELF_HOSTED_URL_MESSAGE),
});
export const nimProviderConfigSchema = z.discriminatedUnion('deployment', [
    nimHostedConfigSchema,
    nimSelfHostedConfigSchema,
]);
/**
 * Runtime validation for `provider === 'nim'` profile settings.
 *
 * Returns an EMPTY array for every other provider — this is a nim-scoped gate,
 * not a general settings validator, so wiring it in cannot change the accepted
 * shape of any existing profile.
 *
 * Single source for two call sites that cannot share a zod pipeline: the
 * settingsSchema refinement below (the PATCH) and aiProfile.service's
 * create/update gates (the MERGED settings object, after the partial update is
 * folded into the stored row).
 */
export function validateNimProfileSettings(settings) {
    if (settings === null || typeof settings !== 'object' || Array.isArray(settings))
        return [];
    const s = settings;
    if (s.provider !== 'nim')
        return [];
    const issues = [];
    const parsed = nimProviderConfigSchema.safeParse(s.providerConfig);
    if (!parsed.success) {
        for (const issue of parsed.error.issues) {
            issues.push({ path: ['providerConfig', ...issue.path], message: issue.message });
        }
    }
    // NIM's hosted chat API caps temperature at 1.0 (OpenAI's 0–2 range does not
    // apply); an out-of-range value is answered with 422, so it is rejected at
    // write time instead of at first dispatch.
    const temperature = s.temperature;
    if (temperature !== undefined && temperature !== null) {
        if (typeof temperature !== 'number' || !Number.isFinite(temperature) || temperature < 0 || temperature > 1) {
            issues.push({ path: ['temperature'], message: 'NIM temperature 0 ile 1 arasında olmalı' });
        }
    }
    const topP = s.topP;
    if (topP !== undefined && topP !== null) {
        if (typeof topP !== 'number' || !Number.isFinite(topP) || topP <= 0 || topP > 1) {
            issues.push({ path: ['topP'], message: 'NIM topP 0 (hariç) ile 1 arasında olmalı' });
        }
    }
    // A NIM profile without a model is not dispatchable, and the failure it
    // produces is unhelpful in a way this gate exists to prevent: the provider
    // reaches NVIDIA with no model, the base class throws an untyped error, the
    // API answers 500 and the fallback chain treats it as retriable. Refusing the
    // WRITE turns a mystery at chat time into a named field at save time.
    //
    // Required for BOTH deployments. Hosted picks from the curated catalogue;
    // self-hosted names whatever model its container was built with — there is no
    // default in either case, and `settings.model` is the only place either is
    // recorded.
    //
    // Note the blast radius this deliberately does NOT have: `model` is a shared
    // settings field, so a rule expressed in `settingsSchema` would apply to every
    // provider — copilot in particular treats an empty model as "use the CLI
    // default". It lives here, inside the nim-scoped gate, for that reason.
    const model = s.model;
    if (typeof model !== 'string' || model.trim() === '') {
        issues.push({
            path: ['model'],
            message: 'NIM profili için model zorunlu — hosted deployment’ta katalogdan seçin, self-hosted’da container model adını yazın',
        });
    }
    return issues;
}
/**
 * Claude CLI runs as a local process under the creating user, so its profile must stay
 * `personal`. ONE validator for the schema refine (create/update PATCH, where an absent
 * visibility cannot be judged) and aiProfile.service (create with the effective default,
 * update on the MERGED row — a stored claude-cli profile flipped to global by a patch that
 * never names the provider is only visible there).
 */
export function validateClaudeCliVisibility(settings, visibility) {
    if (settings === null || typeof settings !== 'object' || Array.isArray(settings))
        return [];
    if (settings.provider !== 'claude-cli')
        return [];
    if (visibility === undefined || visibility === null || visibility === 'personal')
        return [];
    return [{ path: ['visibility'], message: 'Claude CLI provider yalnızca kişisel (personal) profil olabilir' }];
}
const STREAM_ENUM_MESSAGE = "'on' | 'off' string enum olmalı (boolean kabul edilmez)";
// Field bounds. Revised 2026-09-02 against the stored rows (7 profiles exceeded the previous
// timeout 300000 / continuationTimeout 120000 / maxAutopilotContinues min 1 — the editor echoes
// every stored value on save, so a bound below stored data is a 400 on the next unrelated edit):
//   timeout, continuationTimeout ≤ 3_600_000 ms · maxAutopilotContinues ≥ 0 (0 = never continue).
const settingsObjectSchema = z.object({
    provider: z.enum(AI_PROVIDER_TYPES_WRITABLE).optional(),
    providerConfig: providerConfigSchema,
    model: z.string().max(100).optional(),
    reasoningEffort: z.string().max(20).optional(),
    responseLanguage: z.string().max(20).optional(),
    systemPrompt: z.string().max(10000).optional(),
    customInstructionsEnabled: z.boolean().optional(),
    workingDirectory: z.string().max(500).optional(),
    workspacePath: z.string().max(500).optional(),
    timeout: z.number().int().min(1000).max(3_600_000).optional(),
    maxBuffer: z.number().int().min(1024).optional(),
    maxToolRounds: z.number().int().min(1).max(50).optional(),
    maxEmptyArgsRetries: z.number().int().min(1).max(20).optional(),
    autopilot: z.boolean().optional(),
    maxAutopilotContinues: z.number().int().min(0).max(20).optional(),
    continuationTimeout: z.number().int().min(0).max(3_600_000).optional(),
    outputFormat: z.enum(['text', 'json']).optional(),
    // A closed string enum, NOT coerced (#2597): the reader decides streaming by `=== 'on'`, so a
    // boolean used to be stored and then silently fell into the non-stream arm.
    stream: z.enum(['on', 'off'], { errorMap: () => ({ message: STREAM_ENUM_MESSAGE }) }).optional(),
    // Generation parameters — provider-agnostic envelope; NIM narrows temperature/topP to (0,1]
    // inside validateNimProfileSettings (a shared bound would reject OpenAI's legal 0–2).
    temperature: z.number().min(0).max(2).optional(),
    topP: z.number().min(0).max(1).optional(),
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    presencePenalty: z.number().min(-2).max(2).optional(),
    maxCompletionTokens: z.number().int().min(1).max(1_000_000).optional(),
    stopSequences: z.array(z.string().min(1).max(200)).max(16).optional(),
    // History management
    maxHistoryMessages: z.number().int().min(1).max(1000).optional(),
    maxHistoryTokens: z.number().int().min(1).max(10_000_000).optional(),
    // Summary system
    summaryEnabled: z.boolean().optional(),
    summaryThreshold: z.number().int().min(500).max(50000).optional(),
    summaryMaxLength: z.number().int().min(100).max(5000).optional(),
    debugMode: z.boolean().optional(),
    // Claude CLI provider specific fields
    binaryPath: z.string().max(500).optional(),
    permissionMode: z.enum(CLAUDE_CLI_PERMISSION_MODES).optional(),
    // claude-cli only — CLI --cwd flag; workspacePath/workingDirectory field'larından ayrıdır
    cwd: z.string().max(500).optional(),
    allowedTools: z.array(z.string().min(1).max(200)).max(50).optional(),
    disallowedTools: z.array(z.string().min(1).max(200)).max(50).optional(),
    appendSystemPrompt: z.string().max(10000).optional(),
    addDir: z.array(z.string().min(1).max(500)).max(20).optional(),
    noSessionPersistence: z.boolean().optional(),
    // MCP Bridge (claude-cli only) — expose Elmes tool registry via stdio MCP server
    mcpBridgeEnabled: z.boolean().optional(),
    mcpExposedToolNames: z.array(z.string().min(1).max(200)).max(100).optional(),
    // category-level filter — tools matching any of these categories are added to the exposed set
    mcpExposedCategories: z.array(z.string().min(1).max(80)).max(30).optional(),
    // when false (default), tools with dangerLevel='destructive' are stripped from the exposed set
    mcpAllowDestructive: z.boolean().optional(),
}).strict();
// Deliberately NOT declared (strict → 400 by construction):
//   githubToken     — write-only secret, refused by name (gh#2586; aiProfile.service asserts it too)
//   maxTokens       — legacy alias of maxCompletionTokens (4 stored rows, reader ignores it)
//   onBehalfEnforce — marker written by seed/SQL only, never by a caller
//   gateway         — rate-limit / breaker override, seed/SQL only; validated at its reader
//                     (`ai/gateway/index.ts` resolveGatewayConfig), malformed → gateway DEFAULTS
// The refusal is SYMMETRIC for maxTokens/onBehalfEnforce/gateway, which is the round-trip
// gap: a caller that READS a profile carrying one of these three and writes the object
// back unchanged gets 400 too — echoing stored state is indistinguishable from setting
// it. A round-trip must strip them first (dm.issue 1852). githubToken has no read path:
// formatProfile already drops it before the caller sees it (aiProfile.service.ts).
const settingsSchema = settingsObjectSchema.superRefine((value, ctx) => {
    // Same nim gate the service applies on the merge, so the two doors cannot diverge.
    for (const issue of validateNimProfileSettings(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    }
    // `deployment` is NIM's endpoint axis; on any other provider it is inert and refused rather
    // than stored. A provider-less patch inherits the stored provider, so it cannot be judged here
    // (the service sees the merge).
    const pc = value.providerConfig;
    if (value.provider !== undefined &&
        value.provider !== 'nim' &&
        pc !== undefined &&
        'deployment' in pc &&
        pc.deployment !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerConfig', 'deployment'],
            message: "providerConfig.deployment yalnız provider 'nim' için geçerli",
        });
    }
});
const permissionsSchema = z.object({
    allowAllTools: z.boolean().optional(),
    allowAllPaths: z.boolean().optional(),
    allowInternetAccess: z.boolean().optional(),
    allowShellAccess: z.boolean().optional(),
    allowedTools: z.array(z.string().max(200)).optional(),
    deniedTools: z.array(z.string().max(200)).optional(),
    allowedCategories: z.array(z.string().max(50)).optional(),
    allowedPaths: z.array(z.string().max(500)).optional(),
    // Per-request model allowlist (DM #541). The chat gate admits the profile's own
    // effective model PLUS these; an absent/empty list means no override is possible.
    // Free text on purpose — a self-hosted NIM or a custom endpoint serves whatever its
    // container was built with, so catalogue membership cannot be a write-time rule.
    allowedModels: z
        .array(chatModelIdSchema)
        .max(50)
        .refine((list) => new Set(list).size === list.length, 'allowedModels yinelenen model içeremez')
        .optional(),
}).strict();
const cronTriggerSchema = z.object({
    schedule: z.string().min(1).max(100),
    task: z.string().min(1).max(200),
    enabled: z.boolean(),
    description: z.string().max(500).optional(),
    // #2682 — stamped like the event binding below.
    tenantId: z.number().int().positive().optional(),
});
const eventTriggerSchema = z.object({
    event: z.string().min(1).max(200),
    // Strict `===` on top-level payload keys: a nested value could never match (a silently dead trigger).
    filter: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
    task: z.string().min(1).max(200),
    enabled: z.boolean(),
    description: z.string().max(500).optional(),
    // gh#3181 — the service stamps the writer's tenant; a sent value must equal it (checked there).
    tenantId: z.number().int().positive().optional(),
});
const triggersSchema = z.object({
    cron: z.array(cronTriggerSchema).optional(),
    events: z.array(eventTriggerSchema).optional(),
}).strict().nullable();
const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const profileTypeSchema = z.enum(AI_PROFILE_TYPES);
const profileVisibilitySchema = z.enum(['global', 'personal']);
/** Fields a caller may write on BOTH create and update (create makes `name` required). */
const profileWritableFields = {
    name: z.string().min(1).max(100).optional(),
    slug: z.string().min(1).max(100).regex(slugRegex, 'Slug must be lowercase alphanumeric with hyphens only').optional(),
    description: z.string().max(500).optional(),
    avatarEmoji: z.string().max(10).optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be valid hex color').optional(),
    type: profileTypeSchema.optional(),
    visibility: profileVisibilitySchema.optional(),
    settings: settingsSchema.optional(),
    permissions: permissionsSchema.optional(),
    maxDailyTokens: z.number().int().positive().nullable().optional(),
    maxDailyConversations: z.number().int().positive().nullable().optional(),
    maxConcurrentRequests: z.number().int().min(1).max(100).optional(),
    triggers: triggersSchema.optional(),
    isTemplate: z.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
};
function claudeCliVisibilityRefine(d, ctx) {
    for (const issue of validateClaudeCliVisibility(d.settings, d.visibility)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    }
}
// Top-level `.strict()`: an undeclared key is a 400, not a silent drop. Create carries no
// `isActive` / eval / clear* keys — the service's create path does not consume them.
export const createProfileSchema = z.object({
    ...profileWritableFields,
    name: z.string().min(1).max(100),
}).strict().superRefine(claudeCliVisibilityRefine);
export const updateProfileSchema = z.object({
    ...profileWritableFields,
    isActive: z.boolean().optional(),
    // Nightly evaluation flags (roadmap #41 PR-B) — written by the service's simpleFields.
    evalEnabled: z.boolean().optional(),
    evalJudgeProfileId: z.number().int().positive().nullable().optional(),
    canaryModelAlias: z.string().max(100).nullable().optional(),
    // Secret CLEAR intents — the HTTP controller consumes and removes them before the service
    // sees the body (gh#2586); the tool path refuses them.
    clearApiKey: z.boolean().optional(),
    clearServiceAccountJson: z.boolean().optional(),
}).strict().superRefine(claudeCliVisibilityRefine);
export const profileListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
    type: profileTypeSchema.optional(),
    visibility: profileVisibilitySchema.optional(),
    isActive: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
    isTemplate: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
    templateCategory: z.string().max(50).optional(),
    search: z.string().max(100).optional(),
});
export const profileIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
export const importProfileSchema = z.object({
    version: z.literal(1),
    exportedAt: z.string(),
    profile: z.object({
        name: z.string().min(1).max(100),
        slug: z.string().min(1).max(100).regex(slugRegex),
        description: z.string().max(500).nullable(),
        avatarEmoji: z.string().max(10),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        type: profileTypeSchema,
        visibility: profileVisibilitySchema,
        settings: settingsSchema,
        permissions: permissionsSchema,
        triggers: triggersSchema,
        maxDailyTokens: z.number().int().positive().nullable(),
        maxDailyConversations: z.number().int().positive().nullable(),
        maxConcurrentRequests: z.number().int().min(1).max(100),
        isTemplate: z.boolean(),
        templateCategory: z.string().max(50).nullable(),
    }),
});
void true;
void true;
void true;
void true;
void true;
void true;
//# sourceMappingURL=aiProfile.schema.js.map